import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  addDays,
  isoWeek,
  localDate,
  startOfWeek,
  type CreateTimeEntryInput,
  type TimeEntry
} from '@coneno/c-sheet-shared'
import { buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover } from '@/components/ui/popover'
import { usePeople } from '@/hooks/use-people'
import { useCustomers } from '@/hooks/use-customers'
import { useProjects } from '@/hooks/use-projects'
import { useAllTasks, useTasks } from '@/hooks/use-tasks'
import {
  useCopyTimeEntries,
  useCopyDayToClipboard,
  useCreateTimeEntry,
  useDayClipboard,
  useDeleteTimeEntries,
  usePasteDayFromClipboard,
  useRecentDescriptions,
  useRestoreTimeEntries,
  useTimeEntries,
  useUpdateTimeEntry
} from '@/hooks/use-timesheets'
import { CloneEntryDialog } from './components/clone-entry-dialog'
import { EntryForm } from './components/entry-form'
import { WeekGrid } from './components/week-grid'
import { WeekHeader } from './components/week-header'
import { useEntryDraft } from './hooks/use-entry-draft'
import { entryCount, shortDate } from './lib/labels'
import { readStorage, writeStorage } from '@/lib/storage'
import { timesheetPreferenceKeys } from './lib/preferences'

export function TimesheetPage(): React.JSX.Element {
  const pageRef = useRef<HTMLElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedWeek = searchParams.get('week')
  const weekStart = startOfWeek(
    requestedWeek && /^\d{4}-\d{2}-\d{2}$/.test(requestedWeek) ? requestedWeek : localDate()
  )
  const weekEnd = addDays(weekStart, 6)
  function setWeekStart(date: string): void {
    const start = startOfWeek(date)
    setSearchParams(start === startOfWeek(localDate()) ? {} : { week: start })
  }
  const [showEveryone, setShowEveryone] = useState(
    () => readStorage(timesheetPreferenceKeys.everyone) === 'true'
  )
  function changeShowEveryone(value: boolean): void {
    setShowEveryone(value)
    writeStorage(timesheetPreferenceKeys.everyone, String(value))
  }
  const [selectedPersonId, setSelectedPersonId] = useState(() =>
    readStorage(timesheetPreferenceKeys.person)
  )
  const [cloneSourceEntry, setCloneSourceEntry] = useState<TimeEntry | null>(null)

  const people = usePeople()
  const projects = useProjects()
  const customers = useCustomers()
  const allTasks = useAllTasks()
  const descriptions = useRecentDescriptions()
  // The previous week is loaded too, so it can be copied into this one.
  const entries = useTimeEntries({ from: addDays(weekStart, -7), to: weekEnd })
  const createEntry = useCreateTimeEntry()
  const copyEntries = useCopyTimeEntries()
  const dayClipboard = useDayClipboard()
  const copyDay = useCopyDayToClipboard()
  const pasteDay = usePasteDayFromClipboard()
  const updateEntry = useUpdateTimeEntry()
  const deleteEntries = useDeleteTimeEntries()
  const restoreEntries = useRestoreTimeEntries()

  const rememberedPersonExists = people.data?.some((person) => person.id === selectedPersonId)
  const defaultPersonId =
    (rememberedPersonExists ? selectedPersonId : '') ||
    people.data?.find((person) => person.active)?.id ||
    people.data?.[0]?.id ||
    ''
  const draft = useEntryDraft({
    weekStart,
    weekEnd,
    defaultPersonId,
    projects: projects.data,
    allTasks: allTasks.data
  })
  // A remembered project may no longer exist (for example after a restore); only load known ones.
  const tasks = useTasks(
    projects.data?.some((project) => project.id === draft.projectId) ? draft.projectId : ''
  )

  const currentEntries =
    entries.data?.filter((entry) => entry.workDate >= weekStart && entry.workDate <= weekEnd) ?? []
  const previousWeekEntries =
    entries.data?.filter(
      (entry) => entry.workDate >= addDays(weekStart, -7) && entry.workDate < weekStart
    ) ?? []
  const editingEntry = currentEntries.find((entry) => entry.id === draft.editingId)
  const customerNames = new Map(customers.data?.map((customer) => [customer.id, customer.name]))
  const taskNames = new Map(allTasks.data?.map((task) => [task.id, task.name]))

  const error =
    people.error ??
    projects.error ??
    customers.error ??
    tasks.error ??
    allTasks.error ??
    entries.error ??
    createEntry.error ??
    copyEntries.error ??
    copyDay.error ??
    pasteDay.error ??
    updateEntry.error ??
    deleteEntries.error ??
    restoreEntries.error
  const isLoading = people.isLoading || projects.isLoading || entries.isLoading
  const hasActivePeople = people.data?.some((person) => person.active) ?? false
  const hasActiveProjects = projects.data?.some((project) => project.active) ?? false
  const canAddEntry = !isLoading && hasActiveProjects && hasActivePeople
  const isSaving = createEntry.isPending || updateEntry.isPending

  const documentTitle = `Project sheet · Week ${isoWeek(weekStart)}`
  useEffect(() => {
    document.title = `${documentTitle} · c-sheet`
  }, [documentTitle])

  // An arrow key anywhere on the page focuses today's add button, so the grid is reachable
  // without the mouse.
  useEffect(() => {
    function focusAddEntry(event: KeyboardEvent): void {
      if (
        !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) ||
        event.defaultPrevented ||
        event.isComposing ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        draft.open
      ) {
        return
      }

      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"]), a, [role="dialog"], [role="menu"], [role="listbox"], [role="combobox"], [role="slider"], [role="spinbutton"], [role="tablist"], [aria-haspopup], [data-grid-nav]'
        )
      ) {
        return
      }

      const page = pageRef.current
      const button =
        page?.querySelector<HTMLButtonElement>(
          `td[data-day="${localDate()}"] button[data-add-entry]:not(:disabled)`
        ) ?? page?.querySelector<HTMLButtonElement>('button[data-add-entry]:not(:disabled)')
      if (!button) return
      event.preventDefault()
      button.focus()
    }

    window.addEventListener('keydown', focusAddEntry)
    return () => window.removeEventListener('keydown', focusAddEntry)
  }, [draft.open])

  function submitEntry(input: CreateTimeEntryInput): void {
    if (draft.editingId) {
      updateEntry.mutate({ id: draft.editingId, input }, { onSuccess: draft.reset })
      return
    }
    createEntry.mutate(input, {
      onSuccess: () => {
        writeStorage(timesheetPreferenceKeys.person, input.personId)
        writeStorage(timesheetPreferenceKeys.project, input.projectId)
        writeStorage(timesheetPreferenceKeys.task, input.taskId ?? null)
        writeStorage(timesheetPreferenceKeys.billable, String(input.billable))
        draft.afterCreate()
        requestAnimationFrame(() =>
          document.querySelector<HTMLInputElement>('#entry-start-time')?.focus()
        )
      }
    })
  }

  function deleteWithUndo(entriesToDelete: TimeEntry[], message: string): void {
    deleteEntries.mutate(
      entriesToDelete.map((entry) => entry.id),
      {
        onSuccess: (deleted) => {
          draft.reset()
          toast(message, {
            action: { label: 'Undo', onClick: () => restoreEntries.mutate(deleted) }
          })
        }
      }
    )
  }

  function undoCreatedToast(message: string, created: TimeEntry[]): void {
    toast(message, {
      action: {
        label: 'Undo',
        onClick: () => deleteEntries.mutate(created.map((entry) => entry.id))
      }
    })
  }

  function copyPreviousWeek(): void {
    if (!previousWeekEntries.length) return
    copyEntries.mutate(
      previousWeekEntries.map((entry) => ({
        personId: entry.personId,
        projectId: entry.projectId,
        taskId: entry.taskId,
        workDate: addDays(entry.workDate, 7),
        startTime: entry.startTime,
        endTime: entry.endTime,
        description: entry.description,
        billable: entry.billable
      })),
      {
        onSuccess: (created) =>
          undoCreatedToast(
            `${entryCount(previousWeekEntries.length)} copied from the previous week`,
            created
          )
      }
    )
  }

  function clearDay(dayEntries: TimeEntry[], date: string): void {
    if (!dayEntries.length) return
    deleteWithUndo(dayEntries, `${entryCount(dayEntries.length)} cleared from ${shortDate(date)}`)
  }

  function pasteClipboardDay(date: string): void {
    if (!dayClipboard.canPaste) return
    pasteDay.mutate(
      { workDate: date, payload: dayClipboard.payload },
      {
        onSuccess: (created) =>
          undoCreatedToast(`${entryCount(created.length)} pasted into ${shortDate(date)}`, created)
      }
    )
  }

  function copyClipboardDay(date: string): void {
    copyDay.mutate(date, {
      onSuccess: (payload) =>
        toast(`${entryCount(payload.entries.length)} copied from ${shortDate(date)}`)
    })
  }

  function openCloneDialog(entry: TimeEntry): void {
    draft.reset()
    setCloneSourceEntry(entry)
  }

  return (
    <main ref={pageRef} className="min-h-full p-4 sm:p-6">
      <section className="mx-auto flex w-full max-w-7xl flex-col gap-4">
        <CloneEntryDialog entry={cloneSourceEntry} onClose={() => setCloneSourceEntry(null)} />

        <Popover
          open={draft.open}
          onOpenChange={(open) => {
            if (!open) draft.reset()
          }}
        >
          <WeekHeader
            weekStart={weekStart}
            weekEnd={weekEnd}
            onWeekChange={setWeekStart}
            showEveryone={showEveryone}
            onShowEveryoneChange={changeShowEveryone}
            previousWeekCount={previousWeekEntries.length}
            currentWeekCount={currentEntries.length}
            copying={copyEntries.isPending}
            clearing={deleteEntries.isPending}
            onCopyPreviousWeek={copyPreviousWeek}
            onClearWeek={() =>
              deleteWithUndo(
                currentEntries,
                `${entryCount(currentEntries.length)} cleared from week ${isoWeek(weekStart)}`
              )
            }
          />

          <EntryForm
            draft={draft}
            people={people.data}
            projects={projects.data}
            tasks={tasks.data}
            customerNames={customerNames}
            taskNames={taskNames}
            descriptions={descriptions.data ?? []}
            editingEntry={editingEntry}
            isSaving={isSaving}
            deleting={deleteEntries.isPending}
            onPersonChange={(id) => {
              draft.setPersonId(id)
              setSelectedPersonId(id)
            }}
            onSubmit={submitEntry}
            onClone={openCloneDialog}
            onDelete={(entry) => deleteWithUndo([entry], 'Entry deleted')}
          />

          {!isLoading && !error && !canAddEntry && (
            <Card>
              <CardHeader>
                <CardTitle>Set up your first timesheet</CardTitle>
                <CardDescription>
                  Add an active person and project, then record time with the + buttons.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {!hasActivePeople && (
                  <Link className={buttonVariants({ size: 'sm' })} to="/people">
                    Manage people
                  </Link>
                )}
                {!hasActiveProjects && (
                  <Link
                    className={buttonVariants({ size: 'sm', variant: 'outline' })}
                    to="/projects"
                  >
                    Manage projects
                  </Link>
                )}
              </CardContent>
            </Card>
          )}

          <WeekGrid
            weekStart={weekStart}
            entries={currentEntries}
            people={people.data}
            projects={projects.data}
            customers={customers.data}
            tasks={allTasks.data}
            showEveryone={showEveryone}
            onShowEveryone={() => changeShowEveryone(true)}
            isLoading={isLoading}
            error={error}
            hasActiveProjects={hasActiveProjects}
            dayClipboard={dayClipboard}
            copyingDay={copyDay.isPending}
            pastingDay={pasteDay.isPending}
            deleting={deleteEntries.isPending}
            onCopyDay={copyClipboardDay}
            onPasteDay={pasteClipboardDay}
            onClearDay={clearDay}
            onAddForPerson={draft.prepareNew}
            onAddFromRow={draft.prepareFromRow}
            onEdit={draft.edit}
            onClone={openCloneDialog}
            onDelete={(entry) => deleteWithUndo([entry], 'Entry deleted')}
          />
        </Popover>
      </section>
    </main>
  )
}
